import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { User, UserRole, ChurchSettings, CloudProvider, Playlist, MediaType, FileNode, MediaItem, Church, YouTubePlaylist, SpotifyPlaylist } from '../types';
import { en } from '../locales/en.ts';
import { de } from '../locales/de.ts';

interface AppContextType {
    isInitialized: boolean;
    user: User | null;
    originalUser: User | null;
    currentUser: User | null;
    language: 'en' | 'de';
    theme: 'light' | 'dark';
    remoteMappings: { back: string; forward: string; play: string };
    t: typeof en;
    needsChangelog: boolean;
    currentVersion: string;

    filteredUsers: User[];
    usersList: User[];
    filteredPlaylists: Playlist[];
    playlists: Playlist[];
    settings: ChurchSettings;
    currentChurch: Church | undefined; // The full church entity for the current user
    fileSystem: FileNode[];
    churchesList: Church[];
    settingsList: ChurchSettings[]; // Exposed for Super Admin to find specific settings
    mediaList: MediaItem[]; // Media library
    youtubePlaylists: YouTubePlaylist[]; // YouTube playlists
    spotifyPlaylists: SpotifyPlaylist[]; // Spotify playlists

    submitChangelog: (content: string) => Promise<void>;
    login: (email: string, pass: string) => Promise<'SUCCESS' | 'INVALID_EMAIL' | 'INVALID_PASSWORD' | 'SERVER_ERROR'>;
    requestPasswordReset: (email: string) => Promise<void>;
    logout: () => void;
    impersonateUser: (userId: string) => void;
    stopImpersonation: () => void;

    setLanguage: (lang: 'en' | 'de') => Promise<void>;
    toggleTheme: () => Promise<void>;
    updateRemoteMappings: (mappings: { back: string; forward: string; play: string }) => Promise<void>;

    addChurch: (c: Omit<Church, 'id'>) => Promise<void>;
    addChurchWithAdmin: (c: { name: string; address?: string; admin: { name: string; email: string } }) => Promise<{ church: Church; admin: User }>;
    updateChurch: (id: string, c: Partial<Church>) => Promise<void>;
    deleteChurch: (id: string) => Promise<void>;
    updateSettings: (s: Partial<ChurchSettings>, specificChurchId?: string) => Promise<void>;
    updateBaseFolder: (baseFolder: { id: string; name: string; path?: string }) => Promise<void>;
    getBaseFolder: () => Promise<{ baseFolder: { id: string; name: string; path?: string } | null; cloudProvider: CloudProvider; cloudConnected: boolean }>;
    connectCloud: (provider: CloudProvider) => Promise<void>;

    addPlaylist: (p: Playlist) => Promise<void>;
    updatePlaylist: (id: string, p: Partial<Playlist>) => Promise<void>;
    deletePlaylist: (id: string) => Promise<void>;
    addMedia: (media: Omit<MediaItem, 'id'>) => Promise<MediaItem>;
    removeMedia: (id: string) => Promise<void>;
    getPlaylistVideos: (playlistName: string) => MediaItem[];

    // YouTube Playlist Management
    addYouTubePlaylist: (url: string) => Promise<void>;
    removeYouTubePlaylist: (id: string) => Promise<void>;
    refreshYouTubePlaylist: (id: string) => Promise<void>;
    getYouTubePlaylistVideos: (playlistId: string) => MediaItem[];

    // Spotify Playlist Management
    addSpotifyPlaylist: (url: string) => Promise<void>;
    addSpotifyTrack: (url: string) => Promise<void>;
    removeSpotifyPlaylist: (id: string) => Promise<void>;
    refreshSpotifyPlaylist: (id: string) => Promise<void>;
    getSpotifyPlaylistTracks: (playlistId: string) => MediaItem[];

    // Spotify Connection Management
    connectSpotify: () => Promise<void>;
    disconnectSpotify: () => Promise<void>;
    checkSpotifyConnection: () => Promise<{
        connected: boolean; hasValidToken: boolean; hasPremium?: boolean; hasWebPlaybackScopes?: boolean }>;

    addUser: (u: Omit<User, 'id'>) => Promise<User>;
    updateUser: (id: string, u: Partial<User>) => Promise<void>;
    deleteUser: (id: string) => Promise<void>;
    updateProfile: (name: string, password?: string) => Promise<void>;

    addFile: (parentId: string, file: File) => Promise<void>;
    createFolder: (parentId: string, name: string) => Promise<void>;
    fetchRawFiles: (parentId?: string) => Promise<FileNode[]>;
    browseFiles: (parentId: string) => Promise<FileNode[]>;

    // Service Pin Management
    getServicePin: () => Promise<{ pin: string; expiresAt: string } | null>;
    validateServicePin: (churchId: string, pin: string) => Promise<boolean>;

    // Test Email Management
    sendTestEmail: () => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const API_BASE = '/api'; // Relative path using Vite proxy

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isInitialized, setIsInitialized] = useState(false);
    const [user, setUser] = useState<User | null>(null);
    const [originalUser, setOriginalUser] = useState<User | null>(null);
    const [authToken, setAuthToken] = useState<string | null>(() => {
        // Restore token from localStorage on app initialization
        if (typeof window !== 'undefined') {
            return localStorage.getItem('authToken');
        }
        return null;
    });

    const [usersList, setUsersList] = useState<User[]>([]);
    const [churchesList, setChurchesList] = useState<Church[]>([]);
    const [settingsList, setSettingsList] = useState<ChurchSettings[]>([]);
    const [mediaList, setMediaList] = useState<MediaItem[]>([]);
    const [playlists, setPlaylists] = useState<Playlist[]>([]);
    const [youtubePlaylists, setYoutubePlaylists] = useState<YouTubePlaylist[]>([]);
    const [currentVersion, setCurrentVersion] = useState('1.0.0');
    const [needsChangelog, setNeedsChangelog] = useState(false);
    const [spotifyPlaylists, setSpotifyPlaylists] = useState<SpotifyPlaylist[]>([]);
    const [localFileSystem, setLocalFileSystem] = useState<FileNode[]>([]);

    const [youtubeCache, setYoutubeCache] = useState<Record<string, MediaItem[]>>({});

    const [language, setLanguageState] = useState<'en' | 'de'>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('language');
            if (saved === 'en' || saved === 'de') return saved as 'en' | 'de';
        }
        return 'de';
    });
    const [theme, setThemeState] = useState<'light' | 'dark'>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('theme');
            if (saved === 'light' || saved === 'dark') return saved as 'light' | 'dark';
        }
        return 'light';
    });

    const [remoteMappings, setRemoteMappingsState] = useState<{ back: string; forward: string; play: string }>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('remoteMappings');
            if (saved) {
                try {
                    return JSON.parse(saved);
                } catch (e) {
                    console.warn('Failed to parse remoteMappings from localStorage:', e);
                }
            }
        }
        return { back: '', forward: '', play: '' };
    });

    const t = language === 'en' ? en : de;

    // Apply theme class to document on changes and initial mount
    React.useEffect(() => {
        if (typeof document !== 'undefined') {
            if (theme === 'dark') document.documentElement.classList.add('dark');
            else document.documentElement.classList.remove('dark');
        }
    }, [theme]);

    const fetchWithAuth = async (endpoint: string, options: RequestInit = {}, customHeaders: Record<string, string> = {}) => {
        if (!authToken) return null;
        const headers: Record<string, string> = {
            'Authorization': `Bearer ${authToken}`,
            ...customHeaders,
            ...options.headers as Record<string, string>,
        };

        // Do not set Content-Type for FormData (file uploads)
        if (!(options.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
        }

        try {
            const response = await fetch(`${API_BASE}${endpoint}`, { ...options, headers });
            const text = await response.text();
            if (response.status === 401) {
                logout();
            }
            if (!response.ok) {
                // Try to surface backend error message if available
                let message = 'API Error';
                try {
                    message = text ? (JSON.parse(text)?.message || text) : response.statusText;
                } catch {
                    message = text || response.statusText;
                }
                throw new Error(message);
            }
            return text ? JSON.parse(text) : null;
        } catch (error) {
            console.error("Fetch error:", error);
            throw error;
        }
    };

    const loadData = async () => {
        if (!authToken) return;
        try {
            const [users, churches, pls, sets, files, media, ytPlaylists, spotifyPlaylists] = await Promise.all([
                fetchWithAuth('/users'),
                fetchWithAuth('/churches'),
                fetchWithAuth('/playlists'),
                fetchWithAuth('/settings'),
                fetchWithAuth('/files'),
                fetchWithAuth('/media'),
                fetchWithAuth('/youtube/playlists'),
                fetchWithAuth('/spotify/playlists')
            ]);

            if (users) setUsersList(users);
            if (churches) setChurchesList(churches);
            if (pls) setPlaylists(pls);
            if (sets) setSettingsList(sets);
            if (files) setLocalFileSystem(files);
            if (media) setMediaList(media);
            if (ytPlaylists) setYoutubePlaylists(ytPlaylists);
            if (spotifyPlaylists) setSpotifyPlaylists(spotifyPlaylists);
        } catch (e) {
            console.error("Failed to load data", e);
        }
    };

    useEffect(() => {
        if (authToken && isInitialized) loadData();
    }, [authToken, isInitialized]);

    // Restore user data when token is restored from localStorage
    useEffect(() => {
        const restoreUserData = async () => {
            try {
                if (authToken) {
                    // Use the dedicated /auth/me endpoint to get current user data
                    const currentUser = await fetchWithAuth('/auth/me');
                    if (currentUser) {
                        setUser(currentUser);
                        
                        // Set current version and changelog needs from server response
                        if (currentUser.currentVersion) {
                            setCurrentVersion(currentUser.currentVersion);
                        }
                        if (typeof currentUser.needsChangelog === 'boolean') {
                            setNeedsChangelog(currentUser.needsChangelog);
                        }
                        
                        // Apply saved preferences from server
                         if (currentUser.remoteMappings && (currentUser.remoteMappings.back || currentUser.remoteMappings.forward || currentUser.remoteMappings.play)) {
                             setRemoteMappingsState(currentUser.remoteMappings);
                             if (typeof window !== 'undefined') localStorage.setItem('remoteMappings', JSON.stringify(currentUser.remoteMappings));
                         }
                        if (currentUser.language === 'en' || currentUser.language === 'de') {
                            setLanguageState(currentUser.language);
                            if (typeof window !== 'undefined') localStorage.setItem('language', currentUser.language);
                        }
                        if (currentUser.theme === 'light' || currentUser.theme === 'dark') {
                            setThemeState(currentUser.theme);
                            if (typeof window !== 'undefined') localStorage.setItem('theme', currentUser.theme);
                            if (typeof document !== 'undefined') {
                                if (currentUser.theme === 'dark') document.documentElement.classList.add('dark');
                                else document.documentElement.classList.remove('dark');
                            }
                        }
                    }
                }
            } catch (error) {
                console.error('Failed to restore user data:', error);
                // If token is invalid, clear it
                if (authToken) {
                    logout();
                }
            } finally {
                // Always set initialized to true, even if there was an error
                setIsInitialized(true);
            }
        };

        restoreUserData();
    }, []); // Run only once on mount

    // --- FILTERING ---
    const filteredUsers = useMemo(() => {
        if (!user) return [];
        if (user.role === UserRole.SUPER_ADMIN) return usersList;
        return usersList.filter(u => u.churchId === user.churchId);
    }, [user, usersList]);

    const filteredPlaylists = useMemo(() => {
        if (!user) return [];
        if (user.role === UserRole.SUPER_ADMIN) return playlists;
        return playlists.filter(p => p.churchId === user.churchId);
    }, [user, playlists]);

    // Current User's Church Object
    const currentChurch = useMemo(() => {
        if (!user || !user.churchId) return undefined;
        return churchesList.find(c => c.id === user.churchId);
    }, [user, churchesList]);

    // Current User's Church Settings
    const filteredSettings = useMemo(() => {
        if (!user) return {
            id: 'temp',
            churchId: '',
            name: '',
            cloudProvider: CloudProvider.NONE,
            cloudConnected: false,
            isWorshipEnabled: true,
            isMaterialEnabled: true,
            isSpotifyEnabled: true,
            isSlidesEnabled: true,
            youtubePlaylists: [],
            spotifyPlaylists: []
        };
        const targetChurchId = user.churchId;
        let found = settingsList.find(s => s.churchId === targetChurchId);
        if (!found && settingsList.length > 0 && user.role !== UserRole.SUPER_ADMIN) found = settingsList[0];
        // Return a proper fallback instead of 'err'
        return found || {
            id: '',
            churchId: targetChurchId || '',
            name: 'Default',
            cloudProvider: CloudProvider.NONE,
            cloudConnected: false,
            isWorshipEnabled: true,
            isMaterialEnabled: true,
            isSpotifyEnabled: true,
            isSlidesEnabled: true,
            youtubePlaylists: [],
            spotifyPlaylists: []
        };
    }, [user, settingsList]);

    const fileSystem = useMemo(() => localFileSystem, [localFileSystem]);

    // --- YOUTUBE FETCHING ---
    useEffect(() => {
        // Automatically fetch videos for playlists configured in settings
        const fetchVideos = async () => {
            if (!authToken || !filteredSettings.youtubePlaylists) return;

            for (const playlistId of filteredSettings.youtubePlaylists) {
                if (!youtubeCache[playlistId]) {
                    const videos = await fetchWithAuth(`/youtube/playlist/${playlistId}`);
                    if (videos) {
                        setYoutubeCache(prev => ({ ...prev, [playlistId]: videos }));
                    }
                }
            }
        };
        fetchVideos();
    }, [filteredSettings.youtubePlaylists, authToken]);

    const getPlaylistVideos = (playlistId: string): MediaItem[] => {
        return youtubeCache[playlistId] || [];
    };

    // --- ACTIONS ---
    const login = async (email: string, pass: string): Promise<'SUCCESS' | 'INVALID_EMAIL' | 'INVALID_PASSWORD' | 'SERVER_ERROR'> => {
        try {
            const response = await fetch(`${API_BASE}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password: pass })
            });

            if (response.ok) {
                const data = await response.json();
                setUser(data.user);
                setAuthToken(data.token);
                // Apply user preferences if provided
                if (data.user?.language === 'en' || data.user?.language === 'de') {
                    setLanguageState(data.user.language);
                    if (typeof window !== 'undefined') localStorage.setItem('language', data.user.language);
                }
                if (data.user?.theme === 'light' || data.user?.theme === 'dark') {
                    setThemeState(data.user.theme);
                    if (typeof window !== 'undefined') localStorage.setItem('theme', data.user.theme);
                    if (typeof document !== 'undefined') {
                        if (data.user.theme === 'dark') document.documentElement.classList.add('dark');
                        else document.documentElement.classList.remove('dark');
                    }
                }
                // Apply remoteMappings if provided
                 if (data.user?.remoteMappings && (data.user.remoteMappings.back || data.user.remoteMappings.forward || data.user.remoteMappings.play)) {
                     setRemoteMappingsState(data.user.remoteMappings);
                     if (typeof window !== 'undefined') localStorage.setItem('remoteMappings', JSON.stringify(data.user.remoteMappings));
                 }
                setCurrentVersion(data.currentVersion || '1.0.0');
                // Store token in localStorage for persistence
                setNeedsChangelog(data.needsChangelog || false);
                if (typeof window !== 'undefined') {
                    localStorage.setItem('authToken', data.token);
                }
                return 'SUCCESS';
            } else {
                return 'INVALID_PASSWORD';
            }
        } catch (e) {
            return 'SERVER_ERROR';
        }
    };

    const submitChangelog = async (content: string) => {
        await fetchWithAuth('/changelogs', { method: 'POST', body: JSON.stringify({ content }) });
        setNeedsChangelog(false);
    };

    const requestPasswordReset = async (email: string) => {
        await fetch(`${API_BASE}/auth/forgot-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
        });
    };

    const logout = () => {
        setUser(null);
        setOriginalUser(null);
        setAuthToken(null);
        // Clear token from localStorage
        if (typeof window !== 'undefined') {
            localStorage.removeItem('authToken');
        }
        setUsersList([]);
        setChurchesList([]);
        setPlaylists([]);
        setSettingsList([]);
        setMediaList([]);
        setLocalFileSystem([]);
    };

    const impersonateUser = (userId: string) => {
        const target = usersList.find(u => u.id === userId);
        if (target && user?.role === UserRole.SUPER_ADMIN) {
            setOriginalUser(user);
            setUser(target);
        }
    };

    const stopImpersonation = () => {
        if (originalUser) {
            setUser(originalUser);
            setOriginalUser(null);
        }
    };

    const persistPreferences = async (prefs: { language?: 'en' | 'de'; theme?: 'light' | 'dark' }) => {
        // Only try to persist to server if we have a valid auth token
        if (!authToken) {
            console.warn('No auth token available, skipping preference persistence');
            return;
        }

        try {
            await fetchWithAuth('/users/me/preferences', { method: 'PUT', body: JSON.stringify(prefs) });
            // also update local user object if present
            setUser(prev => prev ? { ...prev, ...prefs } as any : prev);
        } catch (e) {
            console.warn('Failed to persist preferences to server:', e);
            // Don't throw - we want local changes to work even if server sync fails
        }
    };

    const setLanguage = async (lang: 'en' | 'de') => {
        const prev = language;
        setLanguageState(lang);
        if (typeof window !== 'undefined') localStorage.setItem('language', lang);
        await persistPreferences({ language: lang });
    };

    const toggleTheme = async () => {
        const next: 'light' | 'dark' = theme === 'light' ? 'dark' : 'light';

        // Always update local state first for immediate feedback
        setThemeState(next);
        if (typeof window !== 'undefined') localStorage.setItem('theme', next);
        if (typeof document !== 'undefined') {
            if (next === 'dark') document.documentElement.classList.add('dark');
            else document.documentElement.classList.remove('dark');
        }

        // Try to persist to server, but don't fail if it doesn't work
        try {
            await persistPreferences({ theme: next });
        } catch (error) {
            console.warn('Failed to persist theme preference to server:', error);
            // Theme change still works locally even if server sync fails
        }
    };

    const updateRemoteMappings = async (mappings: { back: string; forward: string; play: string }) => {
        // Always update localStorage and state first for immediate feedback
        setRemoteMappingsState(mappings);
        if (typeof window !== 'undefined') {
            localStorage.setItem('remoteMappings', JSON.stringify(mappings));
        }

        // Try to persist to server, but don't fail if it doesn't work
        try {
            await fetchWithAuth('/users/me/preferences', {
                method: 'PUT',
                body: JSON.stringify({ remoteMappings: mappings })
            });
        } catch (error) {
            console.warn('Failed to persist remote mappings to server:', error);
            // Remote mappings still work locally even if server sync fails
        }
    };

    const addChurch = async (data: Omit<Church, 'id'>) => {
        const newChurch = await fetchWithAuth('/churches', {
            method: 'POST',
            body: JSON.stringify(data)
        });
        if (newChurch) {
            setChurchesList(prev => [...prev, newChurch]);
            loadData();
        }
    };

    const addChurchWithAdmin = async (data: { name: string; address?: string; admin: { name: string; email: string } }) => {
        const result = await fetchWithAuth('/churches/with-admin', {
            method: 'POST',
            body: JSON.stringify(data)
        });
        if (result) {
            setChurchesList(prev => [...prev, result.church]);
            setUsersList(prev => [...prev, result.admin]);
            loadData();
        }
        return result;
    };

    const updateChurch = async (id: string, updates: Partial<Church>) => {
        const updated = await fetchWithAuth(`/churches/${id}`, {
            method: 'PUT',
            body: JSON.stringify(updates)
        });
        if (updated) {
            setChurchesList(prev => prev.map(c => c.id === id ? updated : c));
            if (updates.adminIds) loadData();
        }
    };
    const deleteChurch = async (id: string) => {
        await fetchWithAuth(`/churches/${id}`, { method: 'DELETE' });
        setChurchesList(prev => prev.filter(c => c.id !== id));
        // Also remove associated settings from local state
        setSettingsList(prev => prev.filter(s => s.churchId !== id));
    };

    const updateSettings = async (newSettings: Partial<ChurchSettings>, specificChurchId?: string) => {
        let settingsId = filteredSettings.id;
        if (specificChurchId) {
            const found = settingsList.find(s => s.churchId === specificChurchId);
            if (found) settingsId = found.id;
        }
        if (!settingsId || settingsId === '') {
            console.warn('No valid settings ID found, skipping update. Settings may not be loaded yet for church:', specificChurchId || user?.churchId);
            return;
        }
        try {
            const updated = await fetchWithAuth(`/settings/${settingsId}`, {
                method: 'PUT',
                body: JSON.stringify(newSettings)
            });
            if (updated) {
                setSettingsList(prev => prev.map(s => s.id === settingsId ? updated : s));
                if (newSettings.cloudConnected === false) {
                    const files = await fetchWithAuth('/files');
                    if (files) setLocalFileSystem(files);
                }
            }
        } catch (error) {
            console.error('Failed to update settings:', error);
        }
    };

    const updateBaseFolder = async (baseFolder: { id: string; name: string; path?: string }) => {
        const baseFolderWithTitle = { ...baseFolder, title: baseFolder.name };
        await fetchWithAuth('/settings/base-folder', {
            method: 'PUT',
            body: JSON.stringify({ baseFolder: baseFolderWithTitle })
        });
        setSettingsList(prev => prev.map(s => {
            if (s.churchId === user?.churchId) {
                return { ...s, baseFolder: baseFolderWithTitle };
            }
            return s;
        }));
        try {
            const updatedFiles = await fetchWithAuth('/files');
            if (updatedFiles) setLocalFileSystem(updatedFiles);
        } catch (e) {
            console.error("Failed to refresh file system after base folder update", e);
        }
    };

    const getBaseFolder = async () => {
        const res = await fetchWithAuth('/settings/base-folder');
        return res;
    };

    const connectCloud = async (provider: CloudProvider) => {
        if (!user?.churchId) return;
        if (provider === CloudProvider.GOOGLE_DRIVE) {
            window.location.href = `${API_BASE}/auth/google?churchId=${user.churchId}`;
        } else if (provider === CloudProvider.SHAREPOINT) {
            window.location.href = `${API_BASE}/auth/microsoft?churchId=${user.churchId}`;
        }
    };

    const addPlaylist = async (playlist: Playlist) => {
        try {
            // Ensure we have a valid churchId
            if (!playlist.churchId) {
                throw new Error('Playlist churchId is required');
            }

            // Ensure we have an auth token
            if (!authToken) {
                throw new Error('No authentication token available');
            }

            const newPl = await fetchWithAuth('/playlists', {
                method: 'POST',
                body: JSON.stringify(playlist)
            });

            if (newPl) {
                setPlaylists(prev => [...prev, newPl]);
            }
        } catch (error) {
            console.error('Failed to add playlist:', error);
            throw error;
        }
    };

    const updatePlaylist = async (id: string, updates: Partial<Playlist>) => {
        const updated = await fetchWithAuth(`/playlists/${id}`, {
            method: 'PUT',
            body: JSON.stringify(updates)
        });
        if (updated) setPlaylists(prev => prev.map(p => p.id === id ? updated : p));
    };

    const deletePlaylist = async (id: string) => {
        try {
            await fetchWithAuth(`/playlists/${id}`, { method: 'DELETE' });
            setPlaylists(prev => prev.filter(p => p.id !== id));
        } catch (e) {
            console.error('Failed to delete playlist', e);
            throw e;
        }
    };

    const addMedia = async (media: Omit<MediaItem, 'id'>) => {
        try {
            const newMedia = await fetchWithAuth('/media', {
                method: 'POST',
                body: JSON.stringify(media)
            });
            if (newMedia) {
                setMediaList(prev => [newMedia, ...prev]);
                return newMedia;
            }
            throw new Error('Failed to create media item');
        } catch (error) {
            console.error('Failed to add media:', error);
        }
    };

    const removeMedia = async (id: string) => {
        try {
            const mediaItem = mediaList.find(m => m.id === id);
            if (mediaItem?.playlistId) {
                throw new Error('Cannot delete video that is part of a YouTube playlist. Delete the playlist instead.');
            }
            await fetchWithAuth(`/media/${id}`, { method: 'DELETE' });
            setMediaList(prev => prev.filter(m => m.id !== id));
        } catch (error) {
            console.error('Failed to remove media:', error);
            throw error;
        }
    };

    // --- YOUTUBE PLAYLIST MANAGEMENT ---
    const extractPlaylistId = (url: string): string | null => {
        const match = url.match(/[?&]list=([^&]+)/);
        return match && match[1] ? match[1] : null;
    };

    const addYouTubePlaylist = async (url: string) => {
        if (!authToken) throw new Error('Please sign in again to import a playlist.');
        const playlistId = extractPlaylistId(url);

        try {
            console.info('[YouTube] Importing playlist…', { url, playlistId });
            if (playlistId) {
                const existing = youtubePlaylists.find(p => p.playlistId === playlistId);
                if (existing) throw new Error('This playlist is already imported');
            }
            await fetchWithAuth('/youtube/playlists/import', {
                method: 'POST',
                body: JSON.stringify({ url })
            });
            const [lists, media] = await Promise.all([
                fetchWithAuth('/youtube/playlists'),
                fetchWithAuth('/media')
            ]);
            if (lists) setYoutubePlaylists(lists);
            if (media) setMediaList(media);
            console.info('[YouTube] Playlist import completed');
        } catch (error) {
            console.error('Failed to add YouTube playlist:', error);
            throw error;
        }
    };

    const removeYouTubePlaylist = async (id: string) => {
        try {
            await fetchWithAuth(`/youtube/playlists/${id}`, { method: 'DELETE' });
            const [lists, media] = await Promise.all([
                fetchWithAuth('/youtube/playlists'),
                fetchWithAuth('/media')
            ]);
            if (lists) setYoutubePlaylists(lists);
            if (media) setMediaList(media);
        } catch (error) {
            console.error('Failed to remove YouTube playlist:', error);
            throw error;
        }
    };

    const refreshYouTubePlaylist = async (playlistId: string) => {
        try {
            await fetchWithAuth(`/youtube/playlists/${playlistId}/refresh`, {
                method: 'POST'
            });
            const [lists, media] = await Promise.all([
                fetchWithAuth('/youtube/playlists'),
                fetchWithAuth('/media')
            ]);
            if (lists) setYoutubePlaylists(lists);
            if (media) setMediaList(media);
        } catch (error) {
            console.error('Failed to refresh YouTube playlist:', error);
            throw error;
        }
    };

    const getYouTubePlaylistVideos = (playlistId: string): MediaItem[] => {
        return mediaList.filter(m => m.playlistId === playlistId);
    };

    // --- SPOTIFY PLAYLIST MANAGEMENT ---
    const addSpotifyPlaylist = async (url: string) => {
        if (!authToken) throw new Error('Please sign in again to import a playlist.');
        try {
            console.info('[Spotify] Importing playlist…', { url });
            await fetchWithAuth('/spotify/playlists/import', {
                method: 'POST',
                body: JSON.stringify({ url })
            });
            const [lists, media] = await Promise.all([
                fetchWithAuth('/spotify/playlists'),
                fetchWithAuth('/media')
            ]);
            if (lists) setSpotifyPlaylists(lists);
            if (media) setMediaList(media);
        } catch (error) {
            console.error('Failed to add Spotify playlist:', error);
            throw error;
        }
    };

    const addSpotifyTrack = async (url: string) => {
        if (!authToken) throw new Error('Please sign in again to import a track.');
        try {
            console.info('[Spotify] Importing track…', { url });
            const result = await fetchWithAuth('/spotify/tracks/import', {
                method: 'POST',
                body: JSON.stringify({ url })
            });

            console.info('[Spotify] Track import result:', result);

            // Refresh media list to include the new track
            const media = await fetchWithAuth('/media');
            if (media) setMediaList(media);
        } catch (error) {
            console.error('Failed to add Spotify track:', error);
            throw error;
        }
    };

    const removeSpotifyPlaylist = async (id: string) => {
        try {
            await fetchWithAuth(`/spotify/playlists/${id}`, { method: 'DELETE' });
            const [lists, media] = await Promise.all([
                fetchWithAuth('/spotify/playlists'),
                fetchWithAuth('/media')
            ]);
            if (lists) setSpotifyPlaylists(lists);
            if (media) setMediaList(media);
        } catch (error) {
            console.error('Failed to remove Spotify playlist:', error);
            throw error;
        }
    };

    const refreshSpotifyPlaylist = async (playlistId: string) => {
        try {
            await fetchWithAuth(`/spotify/playlists/${playlistId}/refresh`, {
                method: 'POST'
            });
            const [lists, media] = await Promise.all([
                fetchWithAuth('/spotify/playlists'),
                fetchWithAuth('/media')
            ]);
            if (lists) setSpotifyPlaylists(lists);
            if (media) setMediaList(media);
        } catch (error) {
            console.error('Failed to refresh Spotify playlist:', error);
            throw error;
        }
    };

    const getSpotifyPlaylistTracks = (playlistId: string): MediaItem[] => {
        return mediaList.filter(m => m.playlistId === playlistId);
    };

    const connectSpotify = async () => {
        if (!user?.churchId) return;
        window.location.href = `${API_BASE}/auth/spotify?churchId=${user.churchId}`;
    };

    const disconnectSpotify = async () => {
        try {
            await fetchWithAuth('/auth/spotify/disconnect', { method: 'POST' });
            // Just disconnect - do not delete playlists and media, they remain but inactive
            // The connection status will be updated by checkSpotifyConnection
        } catch (error) {
            console.error('Failed to disconnect Spotify:', error);
            throw error;
        }
    };

    // Cache for Spotify connection status
    let cachedSpotifyConnection: { connected: boolean; hasValidToken: boolean; hasPremium?: boolean; hasWebPlaybackScopes?: boolean } = { connected: false, hasValidToken: false };
    let lastSpotifyCheckTime = 0;
    const SPOTIFY_CACHE_DURATION = 30000; // 30 seconds

    const checkSpotifyConnection = async (): Promise<{ connected: boolean; hasValidToken: boolean; hasPremium?: boolean; hasWebPlaybackScopes?: boolean }> => {
        try {
            const now = Date.now();

            // Return cached result if still valid
            if ((now - lastSpotifyCheckTime) < SPOTIFY_CACHE_DURATION) {
                return cachedSpotifyConnection;
            }

            const result = await fetchWithAuth('/auth/spotify/status');
            cachedSpotifyConnection = result || { connected: false, hasValidToken: false };
            lastSpotifyCheckTime = now;

            return cachedSpotifyConnection;
        } catch (error) {
            console.error('Failed to check Spotify connection status:', error);
            return { connected: false, hasValidToken: false };
        }
    };

    const addUser = async (userData: Omit<User, 'id'>): Promise<User> => {
        const newUser = await fetchWithAuth('/users', {
            method: 'POST',
            body: JSON.stringify(userData)
        });
        if (newUser) setUsersList(prev => [...prev, newUser]);
        return newUser;
    };

    const updateUser = async (id: string, updates: Partial<User>) => {
        const updated = await fetchWithAuth(`/users/${id}`, {
            method: 'PUT',
            body: JSON.stringify(updates)
        });
        if (updated) setUsersList(prev => prev.map(u => u.id === id ? updated : u));
    };

    const deleteUser = async (id: string) => {
        try {
            await fetchWithAuth(`/users/${id}`, { method: 'DELETE' });
            setUsersList(prev => prev.filter(u => u.id !== id));
        } catch (e) {
            alert("Could not delete user.");
        }
    };

    const updateProfile = async (name: string, password?: string) => {
        if (!user) return;
        const updated = await fetchWithAuth('/users/me', {
            method: 'PUT',
            body: JSON.stringify({ name, password })
        });
        if (updated) {
            setUser(prev => prev ? { ...prev, name } : null);
            setUsersList(prev => prev.map(u => u.id === user.id ? updated : u));
        }
    };

    const addFile = async (parentId: string, file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        if (parentId) formData.append('parentId', parentId);

        await fetchWithAuth('/files', {
            method: 'POST',
            body: formData
        });
    };

    const createFolder = async (parentId: string, name: string) => {
        await fetchWithAuth('/files/create-folder', {
            method: 'POST',
            body: JSON.stringify({ parentId, name })
        });
    };

    const fetchRawFiles = async (parentId: string = 'root') => {
        try {
            const nodes = await fetchWithAuth(`/files?mode=system&parentId=${parentId}`);
            return Array.isArray(nodes) ? nodes : [];
        } catch (e) {
            console.error("Error fetching raw files", e);

            // Handle specific authentication errors with user-friendly messages
            const errorMessage = e instanceof Error ? e.message : String(e);
            if (errorMessage.includes('TOKEN_REFRESH_FAILED')) {
                console.warn('Token refresh failed - user may need to reconnect cloud provider');
                throw new Error('Your cloud connection has expired. Please reconnect your cloud provider in settings.');
            } else if (errorMessage.includes('INSUFFICIENT_PERMISSIONS')) {
                console.warn('Insufficient permissions for cloud access');
                throw new Error('You do not have permission to access this cloud resource. Please check your account permissions.');
            } else if (errorMessage.includes('RATE_LIMITED')) {
                console.warn('Cloud API rate limited');
                throw new Error('Too many requests to the cloud service. Please wait a moment and try again.');
            } else if (errorMessage.includes('INVALID_CREDENTIALS')) {
                console.warn('Invalid cloud credentials');
                throw new Error('Your cloud credentials are invalid. Please reconnect your cloud provider in settings.');
            }
            
            return [];
        }
    };

    // Pending requests cache to prevent duplicate simultaneous API calls
    const pendingBrowseRequests = useMemo(() => new Map<string, Promise<FileNode[]>>(), []);
    // Track last request time for rate limiting
    const lastBrowseRequestTime = useMemo(() => ({ current: 0 }), []);
    const BROWSE_REQUEST_DELAY = 100; // Minimum ms between requests

    const browseFiles = async (parentId: string): Promise<FileNode[]> => {
        // Check if there's already a pending request for this parentId
        if (pendingBrowseRequests.has(parentId)) {
            return pendingBrowseRequests.get(parentId)!;
        }

        // Rate limiting: ensure minimum delay between requests
        const now = Date.now();
        const timeSinceLastRequest = now - lastBrowseRequestTime.current;
        if (timeSinceLastRequest < BROWSE_REQUEST_DELAY) {
            await new Promise(resolve => setTimeout(resolve, BROWSE_REQUEST_DELAY - timeSinceLastRequest));
        }
        lastBrowseRequestTime.current = Date.now();

        // Create the request promise
        const requestPromise = (async () => {
            try {
                const res = await fetchWithAuth(`/files?parentId=${parentId}`);
                if (Array.isArray(res) && res.length > 0 && res[0].children) {
                    return res[0].children;
                }
                if (Array.isArray(res)) return res;
                return [];
            } catch (e) {
                console.error("Error browsing files", e);
                return [];
            } finally {
                // Clean up pending request after a short delay to allow cache reuse
                setTimeout(() => {
                    pendingBrowseRequests.delete(parentId);
                }, 100);
            }
        })();

        // Store the pending request
        pendingBrowseRequests.set(parentId, requestPromise);
        return requestPromise;
    };

    // Service Pin Management
    const getServicePin = async (): Promise<{ pin: string; expiresAt: string } | null> => {
        if (!user?.churchId) {
            return null;
        }

        const churchId = user.churchId;


        try {
            // Check if we're impersonating a user (originalUser exists and is different from current user)
            const isImpersonating = originalUser && originalUser.churchId !== user.churchId;

            const customHeaders: Record<string, string> = {};
            if (isImpersonating && originalUser?.churchId) {
                customHeaders['x-impersonated-church-id'] = user.churchId;
            }

            const result = await fetchWithAuth(`/servicepin/${churchId}`, {}, customHeaders);

            if (result) {
                // Cache the result for 24 hours or until it expires, whichever comes first
                const cachedAt = Date.now();
                const expiresAt = new Date(result.expiresAt);
                const cacheExpiry = new Date(cachedAt + 24 * 60 * 60 * 1000); // 24 hours from now
                const effectiveExpiry = expiresAt < cacheExpiry ? expiresAt : cacheExpiry;

                return result;
            }

            return null;
        } catch (error) {
            return null;
        }
    };

    const validateServicePin = async (churchId: string, pin: string): Promise<boolean> => {
        try {
            const result = await fetchWithAuth('/servicepin/validate', {
                method: 'POST',
                body: JSON.stringify({ churchId, pin })
            });

            return result?.valid || false;
        } catch (error) {
            return false;
        }
    };

    const sendTestEmail = async (): Promise<void> => {
        try {
            await fetchWithAuth('/test-email', {
                method: 'POST'
            });
        } catch (error) {
            console.error('Failed to send test email:', error);
            throw error;
        }
    };

    return (
        <AppContext.Provider value={{
            isInitialized,
            user, originalUser, currentUser: user, language, theme, remoteMappings, t: t as any,
            needsChangelog, currentVersion,
            filteredUsers, usersList, filteredPlaylists, playlists,
            settings: filteredSettings as ChurchSettings, currentChurch, settingsList,
            fileSystem, churchesList, mediaList, youtubePlaylists, spotifyPlaylists,
            login, logout, impersonateUser, stopImpersonation, requestPasswordReset,
            setLanguage, toggleTheme, updateRemoteMappings,
            addChurch, addChurchWithAdmin, updateChurch, deleteChurch, updateSettings, updateBaseFolder, getBaseFolder, connectCloud,
            addPlaylist, updatePlaylist, deletePlaylist, addMedia, removeMedia, getPlaylistVideos,
            addYouTubePlaylist, removeYouTubePlaylist, refreshYouTubePlaylist, getYouTubePlaylistVideos,
            addSpotifyPlaylist, addSpotifyTrack, removeSpotifyPlaylist, refreshSpotifyPlaylist, getSpotifyPlaylistTracks,
            connectSpotify, disconnectSpotify, checkSpotifyConnection,
            addUser: addUser as (u: Omit<User, "id">) => Promise<User>, updateUser, deleteUser, updateProfile,
            addFile, createFolder, fetchRawFiles, browseFiles,
            getServicePin, validateServicePin, sendTestEmail,
            submitChangelog
        }}>
            {children}
        </AppContext.Provider>
    );
};

export const useApp = () => {
    const context = useContext(AppContext);
    if (!context) throw new Error("useApp must be used within AppProvider");
    return context;
};